import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, call } from './client'
import type { NotificationPreferences } from './types'

export const nkeys = {
  all: ['notifications'] as const,
  count: ['notifications', 'count'] as const,
  list: (query: object) => ['notifications', 'list', query] as const,
  prefs: ['notifications', 'preferences'] as const,
}

/** Unread count for the bell: refetched every 60 s while the tab is visible and on focus. */
export function useUnreadCount(enabled: boolean) {
  return useQuery({
    queryKey: nkeys.count,
    enabled,
    queryFn: async () => (await call(api.GET('/notifications', { params: { query: { per_page: 1, unread: true } } }))).unread_count,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: false,
  })
}

export interface NotificationQuery {
  page: number
  per_page: number
  unread?: boolean
}

export function useNotifications(query: NotificationQuery) {
  return useQuery({
    queryKey: nkeys.list(query),
    queryFn: () => call(api.GET('/notifications', { params: { query } })),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
  })
}

export function useMarkRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => call(api.POST('/notifications/{notificationId}/read', { params: { path: { notificationId: id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: nkeys.all }),
  })
}

export function useMarkAllRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => call(api.POST('/notifications/read-all')),
    onSuccess: () => qc.invalidateQueries({ queryKey: nkeys.all }),
  })
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: nkeys.prefs,
    queryFn: () => call(api.GET('/me/notification-preferences')),
  })
}

export function useSavePreferences() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: NotificationPreferences) => call(api.PUT('/me/notification-preferences', { body })),
    onSuccess: (saved) => qc.setQueryData(nkeys.prefs, saved),
  })
}
